import { Helmet } from 'react-helmet-async'
import PageLayout from '../components/layout/PageLayout.jsx'
import SectionHeading from '../components/ui/SectionHeading.jsx'
import { motion } from 'framer-motion'
import placeholderImg from '../assets/manufacturing-hero.jpg'
import bikashRanjanMandalImg from '../assets/bikash-ranjan-mandal.jpg'

const teamMembers = [
  {
    name: 'Bikash Ranjan Mandal',
    position: 'Director / Managing Director',
    photo: bikashRanjanMandalImg,
  },
  {
    name: 'Kamalika Mandal',
    position: 'Director',
    photo: '',
  },
]



export default function About() {
  return (
    <PageLayout>
      <Helmet>
        <title>About Us | Ritik Medicare Pvt. Ltd.</title>
        <meta
          name="description"
          content="Learn about Ritik Medicare Pvt. Ltd.—our vision, mission, core values, and why we deliver pharmaceutical-grade API manufacturing."
        />
      </Helmet>

      <section className="bg-sectionBg">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:py-16">
          <SectionHeading
            eyebrow="Company Overview"
            title="Ritik Medicare Pvt. Ltd."
            subtitle="API Manufacturing & Pharmaceutical Solutions with a documentation-first, quality-led approach."
          />

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55 }}
                className="rounded-3xl border border-slate-200 bg-white/70 backdrop-blur p-6"
              >
                <h3 className="text-xl font-extrabold text-slate-900">Our Promise</h3>
                <p className="mt-3 text-sm sm:text-[16px] text-slate-600 leading-relaxed">
                  We support the full journey—API development, process development, custom synthesis and contract manufacturing—
                  with consistent QC checkpoints and GMP-aligned documentation to help customers succeed in regulated environments.
                </p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    {
                      t: 'Vision',
                      d: 'To become a trusted partner for pharmaceutical-grade API manufacturing with measurable quality and reliability.',
                    },
                    {
                      t: 'Mission',
                      d: 'Deliver scalable, compliant development-to-production workflows—supported by documentation, quality controls and expert consultancy.',
                    },
                  ].map((x) => (
                    <div key={x.t} className="rounded-2xl border border-slate-200 bg-white p-5">
                      <div className="font-extrabold text-slate-900">{x.t}</div>
                      <div className="text-sm text-slate-600 mt-2">{x.d}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: 0.04 }}
                className="rounded-3xl border border-slate-200 bg-white/70 backdrop-blur p-6"
              >
                <div className="font-extrabold text-slate-900">Core Values</div>
                <div className="mt-4 space-y-3">
                  {[
                    {
                      t: 'Quality First',
                      d: 'Consistency through SOPs, in-process controls, and QC checks.',
                    },
                    {
                      t: 'Regulatory Readiness',
                      d: 'Documentation that supports audits, traceability and submissions.',
                    },
                    {
                      t: 'Innovation & Learning',
                      d: 'Continuous improvements in route efficiency and analytical capability.',
                    },
                    {
                      t: 'Reliable Collaboration',
                      d: 'Clear communication and scalable execution from pilot to production.',
                    },
                  ].map((v) => (
                    <div key={v.t} className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="font-bold text-slate-900">{v.t}</div>
                      <div className="text-sm text-slate-600 mt-1">{v.d}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>

          <div className="mt-10 rounded-3xl border border-slate-200 bg-white p-7">
            <h3 className="text-xl font-extrabold text-slate-900">Why Ritik Medicare</h3>
            <p className="mt-3 text-sm sm:text-[16px] text-slate-600 leading-relaxed">
              In pharmaceutical manufacturing, quality and documentation cannot be an afterthought. We build them into
              every phase—from research to process development and on to manufacturing readiness.
            </p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {["QC checkpoints", "GMP-aligned documentation", "Scalable execution"].map((t) => (
                <div key={t} className="rounded-2xl bg-sectionBg p-4 border border-slate-200">
                  <div className="font-extrabold text-slate-900">{t}</div>
                  <div className="text-sm text-slate-600 mt-2">Built for regulated environments.</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10">
            <SectionHeading
              eyebrow="Team"
              title="Meet Our People"
              subtitle="Our capability is powered by cross-functional experts across R&D, Quality, and Manufacturing."
            />

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {teamMembers.map((m, idx) => (
                <motion.div
                  key={m.name}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: idx * 0.04 }}
                  className="rounded-3xl border border-slate-200 bg-white/70 backdrop-blur p-5"
                >
                  <div className="relative">
                    <img
                      src={m.photo}
                      alt={m.name}
                      className="h-64 w-full object-cover object-top rounded-2xl border border-slate-200"
                      loading="lazy"
                    />
                    <div className="absolute -bottom-3 left-5">
                      <div className="h-10 w-10 rounded-full bg-brand-primary/10 border border-brand-primary/20 flex items-center justify-center text-brand-primary font-extrabold text-sm">
                        {m.name.split(' ').slice(0, 2).join('').slice(0, 2).toUpperCase()}
                      </div>
                    </div>
                  </div>

                  <div className="pt-6">
                    <div className="text-lg font-extrabold text-slate-900">{m.name}</div>
                    <div className="mt-2 text-sm text-slate-600 leading-relaxed">{m.position}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  )
}

